import React, { useEffect, useState } from 'react';
import 'twin.macro';
import { v4 as uuid } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { ZoneId } from '@teetsh/app/src/types/zone';
import { Step1 } from './Step1';
import { Step3 } from './Step3';
import { routes } from '@teetsh/app/src/containers/routes';
import { PageTrackerWrapper } from '@teetsh/app/src/queries/user/track';
import useSchoolyears from 'queries/schoolyears/useSchoolyears';
import { closeYearsAvailable } from '../Schoolyear/yearsAvailable';
import SpinnerComponent from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import { useTranslation } from 'react-i18next';
import { gradeSorter } from '../School/New/gradeSorter';
import { useScholarSystem } from 'queries/user/useScholarSystem';
import {
  DEFAULT_CYCLE,
  ScholarSystems,
  getNiveauFromScholarSystem,
} from 'containers/scholarSystem';
import SimpleNarrowPage from 'components/Layouts/SimpleNarrowPage';
import { Step4 } from 'containers/MesAnneesScolaires/New/Step4';
import { CreateSchoolyearOptions } from 'containers/MesAnneesScolaires/types';
import { ClassroomsOrg, School } from 'types/school';
import { Step3bis } from 'containers/MesAnneesScolaires/New/Step3bis';
import { useCreateSchoolyearAndNavigate } from 'containers/MesAnneesScolaires/New/useCreateSchoolyearAndNavigate';

interface ScyZoneYearPayload {
  year?: number;
  label?: string;
  zone: ZoneId;
  classrooms?: Classroom[];
  classroomsOrg?: ClassroomsOrg;
}

type NewSchoolYearStep =
  | 'year'
  | 'holidays'
  | 'grades'
  | 'classroomsOrg'
  | 'keep';

export function NewSchoolyear() {
  const [step, setStep] = useState<NewSchoolYearStep>('year');
  const { data: existingSchoolYears = [], isLoading: isLoadingSchoolyears } =
    useSchoolyears();

  const { t } = useTranslation(['schoolyearSettings', 'common']);
  const { scholarSystem = ScholarSystems.FR } = useScholarSystem();
  const { createSchoolyearAndNavigate } = useCreateSchoolyearAndNavigate();
  const scholarSystemGrades = scholarSystem.niveaux.map(n => n.niveau);

  const [scyZoneYearPayload, setScyZoneYearPayload] =
    useState<ScyZoneYearPayload>({
      year: undefined,
      zone: 'zone-a',
    });
  const {
    data: previousSchoolYears = [],
    isLoading: isLoadingPreviousSchoolyears,
  } = useSchoolyears(
    false,
    true,
    scyZoneYearPayload.year ? scyZoneYearPayload.year - 1 : undefined,
  );

  useEffect(() => {
    const closeYearsAvailableFiltered = closeYearsAvailable.filter(
      y => !existingSchoolYears.map(y1 => y1.year).includes(y.value),
    );
    setScyZoneYearPayload(prev => ({
      ...prev,
      year:
        closeYearsAvailableFiltered.length > 0
          ? closeYearsAvailableFiltered[0].value
          : undefined,
    }));
  }, [existingSchoolYears]);
  const previousClassroomOrg =
    previousSchoolYears[0]?.schools[0]?.classroomsOrg || 'unified';
  useEffect(() => {
    if (
      (scyZoneYearPayload?.classrooms || []).length === 0 &&
      // On cherche seulement à préremplir si on est dans un cas
      // assez classique, sinon il faudrait demander à l'utilisateur
      // quoi choisir, on fera peut-être ça plus tard...
      (previousSchoolYears || []).length === 1 &&
      previousSchoolYears[0].schools.length === 1 &&
      !isLoadingPreviousSchoolyears
    ) {
      setScyZoneYearPayload(prev => ({
        ...prev,
        zone: previousSchoolYears[0]?.zone?.id || prev.zone,
        classrooms: (previousSchoolYears[0]?.schools[0]?.classrooms || []).map(
          c => ({
            id: uuid(),
            name: c.name,
            degree: c.degree,
            cycle: c.cycle,
            workingDays: c.workingDays,
            order: c.order,
            tags: [],
            events: [],
          }),
        ),
        classroomsOrg: previousClassroomOrg,
      }));
    }
  }, [
    scyZoneYearPayload.year,
    previousSchoolYears,
    isLoadingPreviousSchoolyears,
  ]);

  const navigate = useNavigate();
  const newSchoolId = uuid();

  if (isLoadingSchoolyears) {
    return <SpinnerComponent />;
  }
  const previousSchoolyearSingleSchool: School | undefined =
    (previousSchoolYears || []).length === 1 &&
    (previousSchoolYears[0].schools || []).length === 1
      ? {
          ...previousSchoolYears[0].schools[0],
          schoolyearId: previousSchoolYears[0].id,
        }
      : undefined;
  const skipPrefillLastYear = !previousSchoolyearSingleSchool;

  const onSkipPrefillLastYear = (classrooms: Classroom[]) => {
    const scyId = uuid();
    createSchoolyearAndNavigate({
      schoolyear: {
        id: scyId,
        year: scyZoneYearPayload.year!,
        label: `${scyZoneYearPayload.year! - 1} - ${scyZoneYearPayload.year}`,
        zone: {
          id: scyZoneYearPayload.zone,
          holidays: [],
          periodes: [],
        },
        schools: [
          {
            id: newSchoolId,
            classroomsOrg: previousClassroomOrg || 'unified',
            name: 'Mon école',
            pendingInvitations: [],
            classrooms: classrooms || [],
          },
        ],
      },
      options: {
        keepLastYearCarnetsDeNotes: false,
        keepLastYearEmploisDuTemps: false,
        keepLastYearEtablissement: false,
        keepLastYearTags: false,
      },
    });
  };

  return (
    <SimpleNarrowPage tw="mt-6 md:mt-20 pb-20">
      <SimpleNarrowPage.Title>
        {t('schoolyearSettings:creationOfNewSchoolYear')}
      </SimpleNarrowPage.Title>
      {step === 'year' && (
        <PageTrackerWrapper title="Années scolaires - nouvelle - étape 1">
          <Step1
            year={scyZoneYearPayload.year}
            existingYears={existingSchoolYears.map(scy => scy.year)}
            zone={scyZoneYearPayload.zone}
            onChangeYear={y =>
              setScyZoneYearPayload({ ...scyZoneYearPayload, year: y })
            }
            onChangeZone={z =>
              setScyZoneYearPayload({ ...scyZoneYearPayload, zone: z })
            }
            onSubmit={() => setStep('grades')}
            onCancel={() => navigate(routes.schoolyearsSettings)}
          />
        </PageTrackerWrapper>
      )}
      {step === 'grades' && scyZoneYearPayload.year && (
        <PageTrackerWrapper title="Années scolaires - nouvelle - étape 3">
          <SimpleNarrowPage.SubTitle>
            {t('schoolyearSettings:newSchool.description')}
          </SimpleNarrowPage.SubTitle>
          <Step3
            value={(scyZoneYearPayload?.classrooms || []).map(
              c => c.degree || c.name,
            )}
            skipPrefillLastYear={skipPrefillLastYear}
            onCancel={() => setStep('year')}
            onSubmit={async (selectedGrades: string[]) => {
              const sortedGrades = selectedGrades.sort(
                gradeSorter(scholarSystemGrades),
              );
              const classrooms = sortedGrades.map((grade, idx) => {
                const cycle =
                  getNiveauFromScholarSystem(scholarSystem, grade)?.cycle ||
                  DEFAULT_CYCLE;

                return {
                  name: grade,
                  cycle: [cycle],
                  degree: grade,
                  events: [],
                  id: uuid(),
                  order: idx,
                  tags: [], // let the API fill the default tags
                  workingDays: [0, 1, 3, 4],
                } as Classroom;
              });
              setScyZoneYearPayload({
                ...scyZoneYearPayload,
                classrooms,
              });
              if (classrooms.length > 1) {
                setStep('classroomsOrg');
                return;
              }

              if (skipPrefillLastYear) {
                onSkipPrefillLastYear(classrooms);
                return;
              }

              setStep('keep');
            }}
          />
        </PageTrackerWrapper>
      )}
      {step === 'classroomsOrg' && scyZoneYearPayload.year && (
        <PageTrackerWrapper title="Années scolaires - nouvelle - étape 3bis">
          <SimpleNarrowPage.SubTitle>
            {t('schoolyearSettings:newSchool.descriptionClassroomOrg')}
          </SimpleNarrowPage.SubTitle>
          <Step3bis
            value={scyZoneYearPayload.classroomsOrg || 'unified'}
            nextStepLabel={
              skipPrefillLastYear
                ? t('common:buttons.create')
                : t('common:buttons.next')
            }
            onCancel={() => setStep('grades')}
            onSubmit={classroomsOrg => {
              setScyZoneYearPayload({
                ...scyZoneYearPayload,
                classroomsOrg,
              });
              if (skipPrefillLastYear) {
                onSkipPrefillLastYear(scyZoneYearPayload?.classrooms || []);
                return;
              }
              setStep('keep');
              return;
            }}
          />
        </PageTrackerWrapper>
      )}

      {step === 'keep' && scyZoneYearPayload.year && (
        <PageTrackerWrapper title="Années scolaires - nouvelle - étape 4">
          <SimpleNarrowPage.SubTitle>
            {t('schoolyearSettings:keep.description')}
          </SimpleNarrowPage.SubTitle>
          <Step4
            onCancel={() => {
              setStep('grades');
            }}
            previousSchoolyearSingleSchool={{
              schoolId: previousSchoolyearSingleSchool?.id!,
              schoolyearId: previousSchoolyearSingleSchool?.schoolyearId!,
            }}
            onSubmit={async (options: CreateSchoolyearOptions) => {
              const scyId = uuid();
              return createSchoolyearAndNavigate({
                schoolyear: {
                  id: scyId,
                  year: scyZoneYearPayload.year!,
                  label: `${scyZoneYearPayload.year! - 1} - ${
                    scyZoneYearPayload.year
                  }`,
                  zone: {
                    id: scyZoneYearPayload.zone,
                    holidays: [],
                    periodes: [],
                  },
                  schools: [
                    {
                      id: newSchoolId,
                      classroomsOrg:
                        scyZoneYearPayload.classroomsOrg || 'unified',
                      name: 'Mon école',
                      pendingInvitations: [],
                      classrooms: scyZoneYearPayload.classrooms || [],
                    },
                  ],
                },
                options,
              });
            }}
          />
        </PageTrackerWrapper>
      )}
    </SimpleNarrowPage>
  );
}
